import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../ui/Icon';
import { Avatar } from '../ui/Avatar';
import { NewMenu } from '../ui/NewMenu';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { useUIStore } from '../../stores/ui.store';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { CommandPalette } from './CommandPalette';
import type { UserPublic } from '@masamune/shared';
import { useActionPanelStore } from './ActionPanel';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { useNotifications } from '../../hooks/useNotifications';
import { useActionStream } from '../../features/oggi/useActionStream';
import { NEW_ACTIONS, NEW_ACTION_GROUP_ORDER, visibleNewActions } from '../../lib/newActions';
import { frozenModuleForPath, moduleHome } from '../../lib/frozen';

interface RoleOption {
  id: string;
  key: string | null;
  name: string;
  color: string;
  shortLabel: string;
  canSeeMargin: boolean;
  permissions: Array<{ module: string; level: string }>;
}

const PATH_CRUMBS: Record<string, string[]> = {
  '/': ['Operativo', 'Oggi'],
  '/tasks': ['Operativo', 'Task'],
  '/trattative': ['Operativo', 'Trattative'],
  '/crm': ['Operativo', 'Contatti'],
  '/progetti': ['Lavoro', 'Progetti'],
  '/divisions/marketing': ['Divisioni', 'Digital Marketing'],
  '/divisions/product': ['Divisioni', 'Digital Product'],
  '/divisions/design': ['Divisioni', 'Digital Product', 'Digital Design'],
  '/talent': ['Divisioni', 'Talent Sourcing'],
  '/suppliers': ['Risorse', 'Professionisti'],
  '/admin': ['Back-office', 'Amministrazione'],
  '/invoices': ['Back-office', 'Contabilità'],
  '/services': ['Back-office', 'Abbonamenti'],
  '/vault': ['Back-office', 'Password Vault'],
  '/report': ['Back-office', 'Report'],
  '/settings': ['Back-office', 'Impostazioni'],
};

export function Topbar() {
  const location = useLocation();
  const params = useParams();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const demoActive = useAuthStore((s) => s.demoActive);
  const setDemoRole = useAuthStore((s) => s.setDemoRole);
  const clearDemoRole = useAuthStore((s) => s.clearDemoRole);
  const canvas = useUIStore((s) => s.canvas);
  const setCanvas = useUIStore((s) => s.setCanvas);
  const density = useUIStore((s) => s.density);
  const setDensity = useUIStore((s) => s.setDensity);

  const [avatarOpen, setAvatarOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const previousUnreadRef = useRef<number>(0);

  const { data: allRoles } = useQuery({
    queryKey: ['roles', 'switcher'],
    queryFn: () => api.get<RoleOption[]>('/roles'),
    enabled: avatarOpen && demoOpen,
  });

  // Permessi per il menu "+ Nuovo" globale (stesso gating della Sidebar)
  const canWrite = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  };
  const path = location.pathname;

  // Highlight contestuale per voce "+ Nuovo" (dipende dalla route corrente).
  // Resta qui perché è una concern di presentazione legata al path; la fonte
  // dati delle voci è invece condivisa in src/lib/newActions.ts (FASE 5).
  const NEW_ACTION_HIGHLIGHT: Record<string, boolean> = {
    'new-task': path.startsWith('/tasks'),
    'new-project': path.startsWith('/divisions') || path.startsWith('/projects'),
    'new-client': path.startsWith('/crm') || path.startsWith('/clients'),
    'new-quote': path.startsWith('/invoices') || path.startsWith('/quotes') || location.search.includes('tab=documenti'),
    'new-invoice': path.startsWith('/invoices') || location.search.includes('tab=documenti'),
    'attach-subscription': path.startsWith('/services'),
    'new-vendor': path.startsWith('/suppliers'),
    'new-vault': path.startsWith('/vault'),
  };

  // Costruisce i gruppi del NewMenu dalla fonte unica, gated sui permessi.
  const newMenuGroups = useMemo(() => {
    const visible = visibleNewActions(NEW_ACTIONS, canWrite);
    return NEW_ACTION_GROUP_ORDER.map((label) => ({
      label,
      items: visible
        .filter((a) => a.group === label)
        .map((a) => ({
          icon: a.icon,
          title: a.title,
          sub: a.sub,
          onClick: () => openModal(a.modal),
          highlight: NEW_ACTION_HIGHLIGHT[a.key] ?? false,
        })),
    })).filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, path, location.search, openModal]);

  // Query condivisa ['notifications'] (hook estratto, usato anche dalla Dashboard)
  const { data: notifData } = useNotifications();

  // Toast su nuova notifica (rilevata via diff dell'unreadCount)
  useEffect(() => {
    if (!notifData) return;
    const prev = previousUnreadRef.current;
    if (prev > 0 && notifData.unreadCount > prev) {
      // È arrivata almeno una notifica nuova — mostra toast con la più recente
      const latest = notifData.personal.find((n) => !n.readAt);
      if (latest) toast.info('Nuova notifica', latest.message);
    }
    previousUnreadRef.current = notifData.unreadCount;
  }, [notifData]);

  // Pannello azioni "Da fare" (ha sostituito il dropdown campanella): il badge
  // conta solo gli item urgenti (critici + oggi), non tutte le notifiche.
  const { counts: streamCounts } = useActionStream();
  const toggleActionPanel = useActionPanelStore((s) => s.toggle);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) { setAvatarOpen(false); setDemoOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Shortcut ⌘K / Ctrl+K per Command Palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fase 0: su una route in pausa il breadcrumb è quello della home del modulo + "In pausa"
  const frozenModule = frozenModuleForPath(location.pathname);
  const crumbs = frozenModule ? [...(PATH_CRUMBS[moduleHome(frozenModule)] ?? []), 'In pausa']
  : PATH_CRUMBS[location.pathname] ?? (
    location.pathname.startsWith('/tasks/')      ? ['Operativo', 'Task', 'Dettaglio']
    : location.pathname === '/trattative/pipeline' ? ['Operativo', 'Trattative', 'Pipeline']
    : location.pathname.startsWith('/trattative/') ? ['Operativo', 'Trattative', 'Dettaglio']
    : location.pathname.startsWith('/contatti/')   ? ['Operativo', 'Contatti', 'Scheda']
    : location.pathname.startsWith('/clients/')    ? ['Operativo', 'Contatti', 'Cliente']
    : location.pathname.startsWith('/projects/') ? ['Divisioni', 'Progetto']
    : location.pathname.startsWith('/courses/')  ? ['Divisioni', 'Talent', 'Corso']
    : location.pathname.startsWith('/candidates/') ? ['Divisioni', 'Talent', 'Candidato']
    : location.pathname.startsWith('/suppliers/')  ? ['Risorse', 'Professionista']
    /* Documenti: i dettagli appartengono all'Amministrazione (tab Documenti) */
    : location.pathname.startsWith('/invoices/')   ? ['Back-office', 'Amministrazione', 'Fattura']
    : location.pathname.startsWith('/quotes/')     ? ['Back-office', 'Amministrazione', 'Preventivo']
    : location.pathname.startsWith('/services/')   ? ['Back-office', 'Abbonamenti', 'Abbonamento']
    : ['Pagina']
  );

  return (
    <>
    <header className="os-topbar relative">
      <div className="tb">
        <div className="tb-breadcrumbs">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className={i === crumbs.length - 1 ? 'current' : ''}>{c}</span>
              {i < crumbs.length - 1 && <span className="sep">/</span>}
            </span>
          ))}
        </div>

        <div className="tb-search cursor-pointer" onClick={() => setPaletteOpen(true)}>
          <Icon name="search" size={12} />
          <input placeholder="Cerca clienti, progetti, fatture, candidati…" readOnly onFocus={() => setPaletteOpen(true)} />
          <span className="t-kbd">⌘K</span>
        </div>

        {/* Dove atterrano i comandi secondari della pagina quando si guarda dal
            telefono (components/layout/PageActions.tsx). Sul computer è spento
            in CSS e non occupa niente: là i comandi restano nell'intestazione
            della pagina, dove sono sempre stati. */}
        <div id="m-hd-slot" className="m-hd-slot" />

        {/* + Nuovo globale (P2): tutte le creazioni quotidiane, gated sui permessi,
            voce contestuale evidenziata in base alla route corrente */}
        <NewMenu
          size="sm"
          aurora
          groups={newMenuGroups}
        />

        {/* Da fare: apre il pannello azioni (Ctrl+.) — ha sostituito la campanella */}
        {(() => {
          const total = streamCounts.critical + streamCounts.today;
          const showBadge = total > 0;
          const isFresh = notifData && notifData.unreadCount > previousUnreadRef.current;
          return (
            <button
              className="tb-icon-btn"
              onClick={toggleActionPanel}
              title="Le notifiche ora vivono qui"
              aria-label={showBadge ? `${total} azioni da fare` : 'Da fare'}
              style={{ position: 'relative', overflow: 'visible' }}
            >
              <Icon name="zap" size={14} />
              {showBadge && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -3, right: -3,
                    minWidth: 18, height: 18,
                    padding: '0 5px',
                    borderRadius: 999,
                    background: 'var(--alert)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    fontFamily: 'ui-monospace, monospace',
                    display: 'grid',
                    placeItems: 'center',
                    border: '2px solid var(--paper)',
                    boxShadow: 'var(--shadow-1)',
                    lineHeight: 1,
                    zIndex: 2,
                    animation: isFresh ? 'notif-pulse 1.2s ease-in-out 2' : undefined,
                  }}
                >
                  {Math.min(99, total)}
                </span>
              )}
            </button>
          );
        })()}
        <style>{`@keyframes notif-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.25); } }`}</style>

        {/* Menu avatar (P10): identità + aspetto + account + vista demo + uscita */}
        {user && (
          <div ref={avatarRef} className="relative">
            <button className="tb-role" onClick={() => setAvatarOpen(!avatarOpen)} aria-haspopup="menu" aria-expanded={avatarOpen}>
              <Avatar initials={user.initials} size={28} />
              {demoActive && <Chip>DEMO</Chip>}
              <Icon name="chevronD" size={12} />
            </button>
            {avatarOpen && (
              <div className="dropdown" style={{ right: 0, top: 42, width: 290 }}>
                {/* Identità */}
                <div className="px-4 py-3 border-b border-line flex items-center gap-3">
                  <Avatar initials={user.initials} size={34} />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{user.firstName} {user.lastName}</div>
                    <div className="mute text-[12px] flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: user.role.color }} />
                      {user.role.name}
                    </div>
                  </div>
                </div>
                {/* Aspetto */}
                <div className="px-4 py-3 border-b border-line">
                  <div className="t-micro mb-2">Aspetto</div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[13px]">Tema</span>
                    <div className="tweak-seg">
                      <button aria-pressed={canvas === 'light'} onClick={() => setCanvas('light')}><Icon name="sun" size={12} /></button>
                      <button aria-pressed={canvas === 'dark'} onClick={() => setCanvas('dark')}><Icon name="moon" size={12} /></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px]">Densità</span>
                    <div className="tweak-seg">
                      <button aria-pressed={density === 'comfortable'} onClick={() => setDensity('comfortable')}>Comoda</button>
                      <button aria-pressed={density === 'dense'} onClick={() => setDensity('dense')}>Densa</button>
                    </div>
                  </div>
                </div>
                {/* Account */}
                <div className="py-1 border-b border-line">
                  <div className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-paper-2 text-[13px]"
                    onClick={() => { setAvatarOpen(false); openModal('password', { userId: user.id, mode: 'self' }); }}>
                    <Icon name="lock" size={12} className="mute" /> Cambia password
                  </div>
                  {/* Vista demo (collassata: strumento raro) */}
                  <div className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-paper-2 text-[13px]"
                    onClick={() => setDemoOpen((v) => !v)}>
                    <Icon name="eye" size={12} className="mute" />
                    <span className="flex-1">Cambia vista (demo)</span>
                    {demoActive && <Chip>DEMO</Chip>}
                    <Icon name={demoOpen ? 'chevronD' : 'chevronR'} size={12} className="mute" />
                  </div>
                  {demoOpen && (
                    <div className="max-h-[220px] overflow-y-auto border-t border-line bg-paper-2/40">
                      {(allRoles ?? []).map((r) => (
                        <div key={r.id}
                          className="flex items-center gap-2.5 pl-10 pr-4 py-2 cursor-pointer hover:bg-paper-2 text-[13px]"
                          style={{ background: user.role.id === r.id ? 'var(--paper-2)' : undefined }}
                          // `key` arriva dall'API come stringa libera, lo store lo vuole fra i ruoli noti:
                          // è la stessa cosa, ma TypeScript non può saperlo senza questo passaggio.
                          onClick={() => { setDemoRole({ ...r, key: r.key as UserPublic['role']['key'] }, r.permissions.map((p) => ({ module: p.module as ModuleKey, level: p.level as PermissionLevel }))); setAvatarOpen(false); setDemoOpen(false); }}>
                          <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                          <span className="font-semibold flex-1">{r.name}</span>
                          {user.role.id === r.id && <Icon name="check" size={12} />}
                        </div>
                      ))}
                      {demoActive && (
                        <div className="px-4 py-2">
                          <button className="btn btn-sm w-full justify-center" onClick={() => { clearDemoRole(); setAvatarOpen(false); setDemoOpen(false); }}>
                            <Icon name="arrowLeft" size={12} /> Ripristina il mio ruolo reale
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Uscita */}
                <div className="py-1">
                  <div className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-alert-bg text-[13px] text-alert"
                    onClick={() => { useAuthStore.getState().clear(); window.location.replace('/login'); }}>
                    <Icon name="logout" size={12} /> Esci
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>

    <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

// Tag DEMO: stato neutro (non un warning) → ghost viola-soft, coerente col DS.
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="chip ghost mono text-[12px] uppercase tracking-wide" style={{ padding: '1px 7px' }}>
      {children}
    </span>
  );
}
